import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ForgotPasswordDialog from "@/components/dialogs/ForgotPasswordDialog";
import { onClientLoginSuccess, logException } from '@/lib/utils';
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

const AuthDialog = ({ isOpen, onClose, onSuccess }: AuthDialogProps) => {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const { studioName } = useParams();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [token, setToken] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const signIn = async (email: string, password: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CLIENT_AUTH_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid email or password: " + email);
      }

      const data = await response.json();

      setUser(onClientLoginSuccess(data.token));

      onSuccess(email);
    } catch (error) {
      console.error("Error during login:", error);
      logException(error);
      alert(t("authDialog.loginFailed"));
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CLIENT_SIGNUP_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, firstName, lastName, studioName }),
      });

      // Check if response is not OK
      if (!response.ok) {
        const errorMessage = await response.text(); // Get error message from backend
        if (response.status === 409) {
          throw new Error("Email address is already registered");
        }
        throw new Error(errorMessage || "Registration failed");
      }

      const data = await response.json();
      if (data.clientId) {
        setIsLogin(true);
        alert(t("authDialog.registrationSuccessful"));
      }
    } catch (err) {
      console.error("Error during registration:", err);
      logException(err);
      if (err.message === "Email address is already registered") {
        alert(t("authDialog.emailAlreadyExists")); 
      } else {
        alert(t("authDialog.registrationFailed"));
      }
    }
};


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLogin) {
      await signIn(email, password);
    } else {
      await signUp(email, password, firstName, lastName);
    }
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  return (
    <>
      <Dialog open={isOpen && !showForgotPassword} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{isLogin ? t("authDialog.loginTitle") : t("authDialog.signUpTitle")}</DialogTitle>
            <DialogDescription>
              {isLogin ? t("authDialog.loginDescription") : t("authDialog.signUpDescription")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {!isLogin && (
              <div className="space-y-2">
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-2">
                  <div className="flex-1">
                    <Label htmlFor="firstName">{t("authDialog.firstName")}</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={t("authDialog.firstNamePlaceholder")}
                      required={!isLogin}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="lastName">{t("authDialog.lastName")}</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={t("authDialog.lastNamePlaceholder")}
                      required={!isLogin}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t("authDialog.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("authDialog.emailPlaceholder")}
                required
              />
            </div>
            {isLogin && (
              <div className="space-y-2">

                <Label htmlFor="password">{t("authDialog.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="text-sm text-right">
                  <Button
                    variant="link"
                    className="p-0 h-auto font-normal"
                    onClick={handleForgotPasswordClick}
                    type="button"
                  >
                    {t("authDialog.forgotPassword")}
                  </Button>
                </div>
              </div>)}
            <div className="flex flex-col space-y-4">
              <Button type="submit">
                {isLogin ? t("authDialog.loginButton") : t("authDialog.signUpButton")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? t("authDialog.switchToSignUp") : t("authDialog.switchToLogin")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ForgotPasswordDialog
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onBackToLogin={handleBackToLogin}
      />

    </>
  );
};

export default AuthDialog;
